import { FC, useEffect, useState } from 'react';
import {
  ActionableNotification,
  DismissibleTag,
  InlineLoading,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import {
  StateCountyTree,
  countyLabel,
  type StateCountyBucket,
} from 'components/state-county-tree';
import { format0 } from 'utils/number';
import type {
  AddressArea,
  AddressMatchStats,
  StartProcessResponse,
} from './types';
import { areasQuery, fetchLocationFacets, fetchMatchStats } from './atoms';
import { LaunchProcessModal } from './launch-process-modal';
import styles from './page.module.scss';

// "Texas (TX)" → "TX"; "Midland County, TX" → "TX".
const stateCode = (name: string) =>
  /\(([A-Za-z]{2})\)$/.exec(name)?.[1] ?? /,\s*([A-Za-z]{2})$/.exec(name)?.[1];
const stateName = (name: string) => name.replace(/\s*\([A-Za-z]{2}\)$/, '');

// A checked state with none of its counties picked covers the whole state;
// otherwise it covers just those counties.
export function areasFor(
  states: StateCountyBucket[],
  counties: StateCountyBucket[],
  selectedStates: string[],
  selectedCounties: string[]
): AddressArea[] {
  return states
    .filter((s) => selectedStates.includes(s.id))
    .flatMap((s): AddressArea[] => {
      const code = stateCode(s.name);
      if (!code) return [];
      const picked = counties.filter(
        (c) => c.stateId === s.id && selectedCounties.includes(c.id)
      );
      return picked.length
        ? picked.map((c) => ({ state: code, county: countyLabel(c.name) }))
        : [{ state: code, county: null }];
    });
}

// Area picks come in bursts (state, then county); fetch once they settle.
const STATS_DEBOUNCE_MS = 300;

function formatFigure(value: number | null) {
  return value === null ? '—' : format0(value);
}

const MatchStatsTable: FC<{ areas: AddressArea[] }> = ({ areas }) => {
  // The figures are tagged with the scope they describe, so a new pick shows a
  // loader rather than the previous scope's numbers under it.
  const [result, setResult] = useState<{
    query: string;
    stats: AddressMatchStats;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const query = areasQuery(areas);

  useEffect(() => {
    let stale = false;
    const controller = new AbortController();
    setError(null);
    const timer = setTimeout(() => {
      fetchMatchStats(areas, controller)
        .then((data) => !stale && setResult({ query, stats: data }))
        .catch((e: unknown) => {
          if (stale) return;
          setError(e instanceof Error ? e.message : 'Request failed');
        });
    }, STATS_DEBOUNCE_MS);
    return () => {
      stale = true;
      clearTimeout(timer);
      controller.abort();
    };
    // `query` is the areas' identity; the array itself is rebuilt each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  if (error)
    return (
      <p className={styles.addressLine}>Match stats unavailable: {error}</p>
    );
  const stats = result && result.query === query ? result.stats : null;
  if (!stats) return <InlineLoading description="Loading match stats…" />;

  // The standardized pass is skipped on a large scope; the SQL-only identical
  // figure is then an upper bound.
  const skipped = stats.standardizedMatch === null;
  const rows: [string, string][] = [
    [
      'Identical Match',
      skipped
        ? `up to ${format0(stats.identicalMatch)}`
        : format0(stats.identicalMatch),
    ],
    ['Standardized Match', formatFigure(stats.standardizedMatch)],
    ['No Match', formatFigure(stats.noMatch)],
  ];
  return (
    <div className={styles.matchStats}>
      <Table size="sm" aria-label="Match stats">
        <TableHead>
          <TableRow>
            <TableHeader>Match</TableHeader>
            <TableHeader>Addresses</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(([label, value]) => (
            <TableRow key={label}>
              <TableCell>{label}</TableCell>
              <TableCell>{value}</TableCell>
            </TableRow>
          ))}
          <TableRow className={styles.matchStatsTotal}>
            <TableCell>
              <strong>Total matching criteria</strong>
            </TableCell>
            <TableCell>
              <strong>{formatFigure(stats.total)}</strong>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      {stats.deferredEntities > 0 && (
        <p className={styles.addressLine}>
          Over the per-entity combine cap: {format0(stats.deferredEntities)}
        </p>
      )}
      {skipped && (
        <p className={styles.addressLine}>
          Standardized figures are skipped for large scopes.
        </p>
      )}
    </div>
  );
};

// Launches an address run over the picked areas (none = every location). On
// success the caller navigates to the new run's page.
export const AddressScopeModal: FC<{
  open: boolean;
  onClose: () => void;
  onStarted: (proc: StartProcessResponse) => void;
}> = ({ open, onClose, onStarted }) => {
  const [states, setStates] = useState<StateCountyBucket[]>([]);
  const [counties, setCounties] = useState<StateCountyBucket[]>([]);
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const [selectedCounties, setSelectedCounties] = useState<string[]>([]);
  const [facetsState, setFacetsState] = useState<
    'loading' | 'loaded' | 'error'
  >('loading');
  const [facetsError, setFacetsError] = useState<string | null>(null);
  const [reloadFacets, setReloadFacets] = useState(0);
  // Owned here, not inside the tree, so they're not tied to the tree's lifecycle
  // (it unmounts while facets load or error). Reopening the modal, or a Retry
  // after a failed load, deliberately clears them along with the selection.
  const [treeSearch, setTreeSearch] = useState('');
  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!open) return undefined;
    setSelectedStates([]);
    setSelectedCounties([]);
    setTreeSearch('');
    setExpandedIds({});
    setFacetsState('loading');
    setFacetsError(null);
    const controller = new AbortController();
    fetchLocationFacets(controller)
      .then((facets) => {
        if (controller.signal.aborted) return;
        setStates(facets.state?.buckets ?? []);
        setCounties(facets.county?.buckets ?? []);
        setFacetsState('loaded');
      })
      .catch((e: unknown) => {
        if (controller.signal.aborted) return;
        setFacetsError(
          e instanceof Error ? e.message : 'Failed to load locations'
        );
        setFacetsState('error');
      });
    return () => controller.abort();
  }, [open, reloadFacets]);

  const areas = areasFor(states, counties, selectedStates, selectedCounties);
  // All-locations is an explicit choice — the user ticked nothing — not merely
  // the derived areas coming out empty. A ticked state whose facet name can't be
  // parsed (no "(XX)") yields zero areas; that must not silently widen the run to
  // every location, so it's surfaced as unparseable and submit is blocked.
  const hasSelection = selectedStates.length > 0 || selectedCounties.length > 0;
  // Block submit if ANY selected state can't be parsed to a code, not only when
  // every one fails: a mix of parseable + unparseable would otherwise submit and
  // silently drop the unparseable states (the same widening the guard prevents).
  const unparseableState = states.some(
    (s) => selectedStates.includes(s.id) && !stateCode(s.name)
  );
  const unparseableScope =
    hasSelection && (areas.length === 0 || unparseableState);

  // Unticking a county in the tree takes its state with it once that state has
  // no county left, mirroring the chip rule — so the scope returns to all
  // locations rather than silently widening to the whole state. Tradeoff
  // (product-accepted): a whole-state pick narrowed to one county then unticked
  // collapses to all-locations too, not back to the state — we don't track
  // whether a state was picked wholesale vs. auto-added by a county.
  const onCountiesChange = (ids: string[]) => {
    const removed = selectedCounties.filter((v) => !ids.includes(v));
    setSelectedCounties(ids);
    if (removed.length === 0) return;
    const emptied = new Set<string>();
    for (const countyId of removed) {
      const stateId = counties.find((c) => c.id === countyId)?.stateId;
      if (!stateId) continue;
      const stillPicked = counties.some(
        (c) => c.stateId === stateId && ids.includes(c.id)
      );
      if (!stillPicked) emptied.add(stateId);
    }
    if (emptied.size)
      setSelectedStates((prev) => prev.filter((s) => !emptied.has(s)));
  };

  const stateChips = states
    .filter(
      (s) =>
        selectedStates.includes(s.id) &&
        !counties.some(
          (c) => c.stateId === s.id && selectedCounties.includes(c.id)
        )
    )
    .map((s) => ({
      id: `state-${s.id}`,
      label: `${stateName(s.name)} — all ${format0(
        counties.filter((c) => c.stateId === s.id).length
      )} counties`,
      remove: () => setSelectedStates((prev) => prev.filter((v) => v !== s.id)),
    }));
  const countyChips = counties
    .filter((c) => selectedCounties.includes(c.id))
    .map((c) => ({
      id: `county-${c.id}`,
      label: c.name,
      // Reuse the tree's change rule: dropping the state's last county takes the
      // state with it, so removing a chip never widens the scope to the state.
      remove: () =>
        onCountiesChange(selectedCounties.filter((v) => v !== c.id)),
    }));
  const chips = [...stateChips, ...countyChips];

  return (
    <LaunchProcessModal
      open={open}
      size="lg"
      heading="Bulk Dedupe Addresses"
      primaryButtonText="Run Batch"
      submitDisabled={facetsState !== 'loaded' || unparseableScope}
      url="/admin/bulk_dedupe/cluster"
      buildBody={() => {
        // Explicit intent: nothing ticked → all locations; anything ticked →
        // its (parseable) areas. Never widen a ticked-but-unparseable pick to
        // every location.
        return hasSelection
          ? { bulkType: 'addresses', areas }
          : { bulkType: 'addresses', allLocations: true };
      }}
      onClose={onClose}
      onStarted={onStarted}
    >
      {/* Carbon renders a closed Modal's children, so gate the stats query on
          open — it is a slow all-locations query the hub must not fire. An
          unparseable pick derives zero areas, which the stats fetch would read
          as all-locations; don't preview every-location numbers under a scope
          that couldn't be read. */}
      {open && !unparseableScope && <MatchStatsTable areas={areas} />}
      <p className={styles.addressLine}>
        Scope:{' '}
        <strong>
          {unparseableScope
            ? 'Selected locations couldn’t be read'
            : areas.length === 0
            ? 'All locations'
            : `${format0(areas.length)} ${
                areas.length === 1 ? 'location' : 'locations'
              }`}
        </strong>
      </p>
      <section className={styles.locationSection}>
        <p className={styles.locationLabel}>Location (optional)</p>
        <div className={styles.locationTree}>
          {facetsState === 'error' ? (
            <ActionableNotification
              kind="error"
              lowContrast
              hideCloseButton
              title="Couldn’t load locations"
              subtitle={facetsError ?? ''}
              actionButtonLabel="Retry"
              onActionButtonClick={() => setReloadFacets((c) => c + 1)}
            />
          ) : facetsState === 'loading' ? (
            <InlineLoading description="Loading locations…" />
          ) : (
            <StateCountyTree
              states={states}
              counties={counties}
              selectedStates={selectedStates}
              selectedCounties={selectedCounties}
              onStatesChange={setSelectedStates}
              onCountiesChange={onCountiesChange}
              search={treeSearch}
              onSearchChange={setTreeSearch}
              expandedIds={expandedIds}
              onExpandedIdsChange={setExpandedIds}
            />
          )}
        </div>
        {chips.length > 0 && (
          <div className={styles.locationChips}>
            {chips.map((chip) => (
              <DismissibleTag
                key={chip.id}
                type="blue"
                size="sm"
                text={chip.label}
                title={`Remove ${chip.label}`}
                dismissTooltipLabel={`Remove ${chip.label}`}
                onClose={chip.remove}
              />
            ))}
          </div>
        )}
      </section>
    </LaunchProcessModal>
  );
};
